/**
 * LeaveApproverGuard: data-gated access to the Leave Approvals pages.
 *
 * There is no role for line approvers; access comes from the data the user is
 * linked to. While the capabilities load it shows a skeleton, then lets the user
 * through when they are a line approver or a final approver (or the live badge
 * store already counts approval work for them). Everyone else gets a friendly
 * empty state. The API also returns 404 on any leave the caller can't see, so
 * this guard is for UX, not security.
 */

import type { ReactNode } from 'react';
import { IconChecklist, IconAlertCircle } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Card, Skeleton, Stack } from '@/components/ui';
import { EmptyState } from '@/components/ui/data-display';
import { useLeaveApprovalBadgesStore } from '@/domain/badges/leaveApprovalBadgesStore';
import { useLeaveApprovalCapabilities } from './useLeaveApprovalCapabilities';

export function LeaveApproverGuard({ children }: { children: ReactNode }) {
  const { data, isLoading, isError, refetch } = useLeaveApprovalCapabilities();
  const myPending = useLeaveApprovalBadgesStore((s) => s.myPendingApprovals);
  const myWaiting = useLeaveApprovalBadgesStore((s) => s.myWaitingOnEmployee);

  if (isLoading) {
    return (
      <LayoutContainer>
        <Stack gap="md">
          <Skeleton height={36} width="30%" />
          <Skeleton height={20} width="45%" />
          <Skeleton height={44} radius="md" />
          <Skeleton height={320} radius="md" />
        </Stack>
      </LayoutContainer>
    );
  }

  const allowed =
    !!data?.isLineApprover ||
    !!data?.isFinalApprover ||
    (data?.myPendingCount ?? 0) > 0 ||
    (data?.myWaitingOnEmployeeCount ?? 0) > 0 ||
    myPending > 0 ||
    myWaiting > 0;

  if (allowed) return <>{children}</>;

  if (isError) {
    return (
      <LayoutContainer>
        <Card withBorder radius="md" p="xl">
          <EmptyState
            icon={<IconAlertCircle size={48} />}
            title="We couldn't check your approver access"
            description="Please try again in a moment."
            actionLabel="Try again"
            onAction={() => void refetch()}
          />
        </Card>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <Card withBorder radius="md" p="xl">
        <EmptyState
          icon={<IconChecklist size={48} />}
          title="You're not set up as a leave approver"
          description="When HR names you as someone's line approver, the leave waiting for you shows up here."
        />
      </Card>
    </LayoutContainer>
  );
}
